import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search, Sparkles } from "lucide-react";
import BlogPostsGrid from "./BlogPostsGrid";
import { getAdminBlogs } from "../../admin/adminApis";
import SEO from "../SEO";

interface Blog {
  _id: string;
  title: string;
  description: string;
  content: string;
  thumbnailUrl?: string;
  category?: string;
  createdAt: string;
}

const BlogSection: React.FC = () => {
  const navigate = useNavigate();
  const categories = ["All", "Yoga", "Meditation", "Wellness", "Nutrition", "Mindfulness"];

  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const postsPerPage = 6;

  useEffect(() => {
    const fetchAllBlogs = async () => {
      try {
        const res: any = await getAdminBlogs();
        if (res.blogs) setBlogs(res.blogs);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllBlogs();
  }, []);

  const filteredPosts = blogs.filter((post) => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 bg-white">
        <div className="w-10 h-10 border-4 border-[#2C4A3B]/20 border-t-[#2C4A3B] rounded-full animate-spin mb-4" />
        <p className="text-[#3B5750] font-semibold text-sm">Loading wellness articles...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SEO
        title="Wellness Blog & Insights | MetaFit Wellness"
        description="Explore holistic wellness articles, mindful living guides, yoga practices, nutrition tips, and healing insights from certified MetaFit experts."
        keywords="wellness blog, holistic health articles, yoga tips, nutrition advice, mental wellness, mindfulness, healing practices"
        url="https://www.metafitwellness.com/blog"
        canonical="https://www.metafitwellness.com/blog"
        image="https://www.metafitwellness.com/metafit.webp"
        schema={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "MetaFit Wellness Blog",
          description: "Holistic wellness articles, mindful living guides, and healing insights.",
          url: "https://www.metafitwellness.com/blog",
          publisher: {
            "@type": "Organization",
            name: "MetaFit Wellness",
            logo: {
              "@type": "ImageObject",
              url: "https://www.metafitwellness.com/metalogo.webp",
            },
          },
        }}
      />

      {/* Header Section */}
      <div
        className="relative border-b border-[#2C4A3B]/10 bg-white"
        style={{ paddingTop: "20px", paddingBottom: "18px" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-[#15302B] tracking-tight leading-tight">
            Wellness <span className="text-[#2C4A3B]">Insights</span>
          </h1>

          <p className="text-xs sm:text-sm text-[#3B5750] max-w-lg mx-auto mt-1 leading-snug">
            Discover expert insights, practical lifestyle tips, and holistic healing guides for mind, body, and soul.
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-3 pb-8 sm:pt-4 sm:pb-8">
        {/* Filter and Search Bar */}
        <div className="flex flex-col md:flex-row justify-between mb-4 sm:mb-5 gap-3 md:items-center">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(category);
                  setCurrentPage(1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className={`px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer ${selectedCategory === category
                    ? "bg-[#2C4A3B] text-white shadow-md scale-105"
                    : "bg-white text-[#3B5750] border border-[#2C4A3B]/15 hover:bg-[#E8F0EC]"
                  }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search articles..."
              className="w-full pl-10 pr-4 py-2 sm:py-2.5 bg-white border border-[#2C4A3B]/15 rounded-xl text-sm focus:outline-none focus:border-[#2C4A3B] focus:ring-2 focus:ring-[#2C4A3B]/10 shadow-sm transition-all"
            />
          </div>
        </div>

        {/* Blog Grid - Navigates directly to dedicated page */}
        {currentPosts.length > 0 ? (
          <BlogPostsGrid
            currentPosts={currentPosts}
            onBlogClick={(blog) => {
              navigate(`/blog/${blog._id}`, { state: { blog } });
            }}
          />
        ) : (
          <div className="text-center py-16 bg-white rounded-3xl border border-[#2C4A3B]/10 p-8 max-w-lg mx-auto">
            <p className="text-lg font-bold text-[#15302B] mb-1">No articles found</p>
            <p className="text-sm text-[#4A5D57] mb-4">Try searching with different keywords or switch categories.</p>
            <button
              onClick={() => {
                setSelectedCategory("All");
                setSearchTerm("");
              }}
              className="px-4 py-2 bg-[#2C4A3B] text-white rounded-xl text-xs font-semibold hover:bg-[#1E3E34] transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap justify-center items-center gap-2 mt-8 sm:mt-10">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-xl bg-white border border-[#2C4A3B]/15 text-[#2C4A3B] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#E8F0EC] transition-colors shadow-sm"
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => handlePageChange(p)}
                className={`w-9 h-9 rounded-xl text-xs sm:text-sm font-bold transition-all shadow-sm ${currentPage === p
                    ? "bg-[#2C4A3B] text-white scale-105"
                    : "bg-white text-[#3B5750] border border-[#2C4A3B]/15 hover:bg-[#E8F0EC]"
                  }`}
              >
                {p}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-xl bg-white border border-[#2C4A3B]/15 text-[#2C4A3B] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#E8F0EC] transition-colors shadow-sm"
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogSection;
